import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./MobileNumberWheelPicker.module.css";

const decimals = (value) => String(value).split(".")[1]?.length || 0;
const formatValue = (value, step) => Number(value).toLocaleString("fr-FR", { minimumFractionDigits: decimals(step), maximumFractionDigits: decimals(step) });
const OPTION_HEIGHT = 64;
const WINDOW_RADIUS = 20;

export function getPickerWindow(optionCount, centerIndex, radius = WINDOW_RADIUS) {
  const start = Math.max(0, centerIndex - radius);
  const end = Math.min(optionCount, centerIndex + radius + 1);
  return { start, end };
}

export default function MobileNumberWheelPicker({ open, title, value, min, max, step, unit, onCancel, onConfirm }) {
  const options = useMemo(() => Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, index) => Number((min + index * step).toFixed(decimals(step)))), [max, min, step]);
  const nearestValue = useMemo(() => options.reduce((nearest, option) => Math.abs(option - Number(value)) < Math.abs(nearest - Number(value)) ? option : nearest, options[0]), [options, value]);
  const [selectedValue, setSelectedValue] = useState(nearestValue);
  const [visibleWindow, setVisibleWindow] = useState(() => getPickerWindow(options.length, 0));
  const wheelRef = useRef(null);
  const scrollFrameRef = useRef(null);

  const handleWheelScroll = (event) => {
    if (scrollFrameRef.current) cancelAnimationFrame(scrollFrameRef.current);
    const scrollElement = event.currentTarget;
    scrollFrameRef.current = requestAnimationFrame(() => {
      const optionIndex = Math.max(0, Math.min(options.length - 1, Math.round(scrollElement.scrollTop / OPTION_HEIGHT)));
      setSelectedValue(options[optionIndex]);
      setVisibleWindow(getPickerWindow(options.length, optionIndex));
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => event.key === "Escape" && onCancel();
    window.addEventListener("keydown", handleKeyDown);
    return () => { if (scrollFrameRef.current) cancelAnimationFrame(scrollFrameRef.current); window.removeEventListener("keydown", handleKeyDown); };
  }, [nearestValue, onCancel, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const selectedIndex = Math.max(0, options.indexOf(nearestValue));
    setSelectedValue(nearestValue);
    setVisibleWindow(getPickerWindow(options.length, selectedIndex));
    if (wheelRef.current) wheelRef.current.scrollTop = selectedIndex * OPTION_HEIGHT;
  }, [nearestValue, open, options]);

  const visibleOptions = options.slice(visibleWindow.start, visibleWindow.end);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="number-wheel-title">
      <header className={styles.header}><button type="button" onClick={onCancel}>Annuler</button><div><span>RÉGLER LA VALEUR</span><h2 id="number-wheel-title">{title}</h2></div><button type="button" className={styles.confirmTop} onClick={() => onConfirm(selectedValue)}>OK</button></header>
      <div className={styles.valuePreview} aria-live="polite"><strong>{formatValue(selectedValue, step)}</strong><span>{unit}</span></div>
      <label className={styles.accessibleValueLabel}>
        {title}
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={selectedValue}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            if (!Number.isFinite(nextValue)) return;
            const nextIndex = options.reduce((nearestIndex, option, index) => Math.abs(option - nextValue) < Math.abs(options[nearestIndex] - nextValue) ? index : nearestIndex, 0);
            setSelectedValue(options[nextIndex]);
            setVisibleWindow(getPickerWindow(options.length, nextIndex));
            if (wheelRef.current) wheelRef.current.scrollTop = nextIndex * OPTION_HEIGHT;
          }}
        />
      </label>
      <div className={styles.wheelShell}><div className={styles.selectionBand} aria-hidden="true" /><div ref={wheelRef} className={styles.wheel} role="listbox" aria-label={title} onScroll={handleWheelScroll}><div className={styles.wheelCenterSpacer} /><div style={{ height: visibleWindow.start * OPTION_HEIGHT }} aria-hidden="true" />{visibleOptions.map((option, visibleIndex) => { const selected = option === selectedValue; const optionIndex = visibleWindow.start + visibleIndex; return <button type="button" role="option" aria-selected={selected} className={`${styles.wheelOption} ${selected ? styles.wheelOptionSelected : ""}`} key={option} onClick={() => { setSelectedValue(option); if (wheelRef.current) wheelRef.current.scrollTo({ top: optionIndex * OPTION_HEIGHT, behavior: "smooth" }); }}>{formatValue(option, step)}</button>; })}<div style={{ height: (options.length - visibleWindow.end) * OPTION_HEIGHT }} aria-hidden="true" /><div className={styles.wheelCenterSpacer} /></div></div>
      <button type="button" className={styles.confirmButton} onClick={() => onConfirm(selectedValue)}>Valider {formatValue(selectedValue, step)} {unit}</button>
    </div>, document.body
  );
}
